import { InstrumentSparklineData } from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { TooltipSparkline } from './TooltipSparkline'

const TREND_COLOR_POSITIVE = '#6B8CAE'
const TREND_COLOR_NEGATIVE = '#D4A574'
const TOOLTIP_BORDER = '#2a2a2a'
const MUTED_TEXT = '#888888'
const SECONDARY_MUTED = '#666666'

const SparklineSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  padding: 0.5rem;
`

const LoadingText = styled.span`
  font-size: 0.75rem;
  color: ${MUTED_TEXT};
  font-style: italic;
`

const NoDataMessage = styled.div`
  font-size: 0.75rem;
  color: ${SECONDARY_MUTED};
  text-align: center;
  padding: 0.75rem 0.5rem;
`

const DatapointCount = styled.div`
  font-size: 0.625rem;
  color: ${SECONDARY_MUTED};
  text-align: center;
  padding-top: 0.25rem;
`

const ActionsHint = styled.div`
  font-size: 0.625rem;
  color: ${SECONDARY_MUTED};
  text-align: center;
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${TOOLTIP_BORDER};
`

function AnimatedLoadingText({ message }: { message: string }) {
  const [dots, setDots] = useState('.')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return '.'
      })
    }, 400)
    return () => clearInterval(interval)
  }, [])

  return <LoadingText>{message + dots}</LoadingText>
}

interface SparklineTooltipContentProps {
  sparklineData: InstrumentSparklineData[]
  isLoading: boolean
  datapointCount: number
  error?: boolean
  animateLoading?: boolean
  actionsHint?: React.ReactNode
  showLastMeasured?: boolean
  loadingMessage?: string
}

export function SparklineTooltipContent({
  sparklineData,
  isLoading,
  datapointCount,
  error = false,
  animateLoading = false,
  actionsHint,
  showLastMeasured = true,
  loadingMessage,
}: SparklineTooltipContentProps) {
  const resolvedLoadingMessage = loadingMessage
    ? loadingMessage
    : animateLoading
      ? 'Fetching live data'
      : 'Loading sensor data...'

  return (
    <>
      <SparklineSection>
        {isLoading ? (
          <LoadingContainer>
            {animateLoading ? (
              <AnimatedLoadingText message={resolvedLoadingMessage} />
            ) : (
              <LoadingText>{resolvedLoadingMessage}</LoadingText>
            )}
          </LoadingContainer>
        ) : error ? (
          <NoDataMessage>Unable to load time series data</NoDataMessage>
        ) : sparklineData.length === 0 ? (
          <NoDataMessage>No recent data available</NoDataMessage>
        ) : (
          sparklineData.map((data: InstrumentSparklineData) => {
            const isPositiveTrend = data.currentValue >= data.previousValue
            const trendColor = isPositiveTrend
              ? TREND_COLOR_POSITIVE
              : TREND_COLOR_NEGATIVE
            return (
              <TooltipSparkline
                key={data.id}
                piTag={data.piTag}
                data={data.data}
                currentValue={data.currentValue}
                previousValue={data.previousValue}
                lastMeasuredAt={data.lastMeasuredAt}
                unit={data.unit}
                color={trendColor}
                showLastMeasured={showLastMeasured}
              />
            )
          })
        )}
      </SparklineSection>

      {datapointCount > 3 && (
        <DatapointCount>
          +{datapointCount - 3} more datapoint
          {datapointCount - 3 > 1 ? 's' : ''}
        </DatapointCount>
      )}

      {actionsHint && <ActionsHint>{actionsHint}</ActionsHint>}
    </>
  )
}
